import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TENANT_COMPANY_CODE_SHORT } from '../../../config/tenant';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    PersonAdd as PersonAddIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked,
    Draw as DrawIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { CSEP_STORE, ENERGY_STORE, JHA_STORE } from '../../../config/constants';
import { ensureCsepStructure } from '../utils/csepRecordFactory';
import {
    CONFINED_SPACE_TYPES,
    PERMIT_TYPES,
    SPECIAL_REQUIREMENTS,
    VERIFICATION_ITEMS,
    AIR_CONTAMINANTS
} from '../utils/constants';
import CsepStepHeader from '../CsepStepHeader';
import SignatureModal from '../../jha/SignatureModal';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import axiosInstance from '../../../axiosInstance';

export default function CsepStepTwo() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { csep = null, isNew = false, viewMode = false, fromJha = false, jhaId = null } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [csepData, setCsepData] = useState(null);
    const [energyList, setEnergyList] = useState([]);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [verificationSignModalOpen, setVerificationSignModalOpen] = useState(false);
    const [activeVerificationId, setActiveVerificationId] = useState(null);
    const [attendantsModalOpen, setAttendantsModalOpen] = useState(false);
    const [checkedByModalOpen, setCheckedByModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const recordIdRef = useRef(csep?.uuid || null);

    const isSubmitted = csepData?.status === 'submitted' || csepData?.status === 'completed';
    const isReadOnly = isSubmitted || viewMode;

    useEffect(() => {
        loadData();
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();
        const allEnergy = await db.getAll(ENERGY_STORE);
        setEnergyList(allEnergy || []);

        const tx = db.transaction(CSEP_STORE, 'readonly');
        const store = tx.objectStore(CSEP_STORE);
        let currentRecord = null;

        if (recordIdRef.current) currentRecord = await store.get(recordIdRef.current);
        if (!currentRecord && csep?.uuid) currentRecord = await store.get(csep.uuid);
        if (!currentRecord) { setLoading(false); navigate('/csep'); return; }

        recordIdRef.current = currentRecord.uuid;
        setCsepData(ensureCsepStructure(currentRecord));
        setLoading(false);
    }, [csep, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) { setSaving(false); return; }
        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;
        const db = await initializeDB();
        const tx = db.transaction(CSEP_STORE, 'readwrite');
        await tx.objectStore(CSEP_STORE).put(recordToSave);
        await tx.done;
        setSaving(false);
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        if (isReadOnly) { setCsepData(record); return; }
        const recordWithTimestamp = { ...record, updatedAt: new Date().toISOString() };
        setCsepData(recordWithTimestamp);
        pendingRecordRef.current = recordWithTimestamp;
        setSaving(true);
        if (options.immediate) { flushSave(); return; }
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => flushSave(), 400);
    }, [flushSave, isReadOnly]);

    const updateStepTwo = (field, value) => {
        if (!csepData || isReadOnly) return;
        scheduleSave({ ...csepData, stepTwo: { ...csepData.stepTwo, [field]: value } });
    };

    const updateNestedField = (parent, field, value) => {
        if (!csepData || isReadOnly) return;
        scheduleSave({
            ...csepData,
            stepTwo: { ...csepData.stepTwo, [parent]: { ...csepData.stepTwo[parent], [field]: value } }
        });
    };

    const toggleConfinedSpaceType = (typeId) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.confinedSpaceTypes || [];
        const updated = current.includes(typeId) ? current.filter(t => t !== typeId) : [...current, typeId];
        updateStepTwo('confinedSpaceTypes', updated);
    };

    const toggleSpecialRequirement = (reqId, field, value) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.specialRequirements || {};
        const reqState = current[reqId] || { required: false, verified: false };
        const newValue = value !== undefined ? value : !reqState[field];
        scheduleSave({
            ...csepData,
            stepTwo: {
                ...csepData.stepTwo,
                specialRequirements: { ...current, [reqId]: { ...reqState, [field]: newValue } }
            }
        });
    };

    const updateVerification = (itemId, field, value) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.verifications || {};
        const itemState = current[itemId] || { date: '', signature: '', signedBy: '' };
        scheduleSave({
            ...csepData,
            stepTwo: { ...csepData.stepTwo, verifications: { ...current, [itemId]: { ...itemState, [field]: value } } }
        });
    };

    const handleVerificationSign = (itemId) => {
        setActiveVerificationId(itemId);
        setVerificationSignModalOpen(true);
    };

    const handleVerificationSignatureSave = (signatureData) => {
        if (!activeVerificationId || isReadOnly) return;
        const supervisorName = csepData.stepOne?.entrySupervisorName || '';
        const today = dayjs().format('YYYY-MM-DD'); // local day (not UTC)
        const current = csepData.stepTwo.verifications || {};
        const itemState = current[activeVerificationId] || { date: '', signature: '', signedBy: '' };
        scheduleSave({
            ...csepData,
            stepTwo: {
                ...csepData.stepTwo,
                verifications: {
                    ...current,
                    [activeVerificationId]: {
                        ...itemState,
                        signature: signatureData,
                        signedBy: supervisorName,
                        date: itemState.date || today
                    }
                }
            }
        });
        setVerificationSignModalOpen(false);
        setActiveVerificationId(null);
    };

    const handleVerificationSignatureClear = (itemId) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.verifications || {};
        const itemState = current[itemId] || { date: '', signature: '', signedBy: '' };
        scheduleSave({
            ...csepData,
            stepTwo: {
                ...csepData.stepTwo,
                verifications: { ...current, [itemId]: { ...itemState, signature: '', signedBy: '' } }
            }
        });
    };

    const handleAttendantsSelect = (selectedEmployees) => {
        if (isReadOnly) return;
        const attendants = selectedEmployees.map(emp => ({
            id: emp.id || emp.ID || emp.EE,
            name: emp.FullName || emp.fullName || emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            type: emp._type || emp.type || 'employee'
        }));
        updateStepTwo('attendants', attendants);
        setAttendantsModalOpen(false);
    };

    const handleCheckedBySelect = (selectedEmployees) => {
        if (isReadOnly) return;
        if (selectedEmployees.length > 0) {
            const emp = selectedEmployees[0];
            updateStepTwo('atmosphereCheckedBy', emp.FullName || emp.fullName || emp.name || '');
        }
        setCheckedByModalOpen(false);
    };

    const removeAttendant = (attendantId) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.attendants || [];
        updateStepTwo('attendants', current.filter(a => a.id !== attendantId));
    };

    const updateAirReading = (contaminantId, checkNum, field, value) => {
        if (isReadOnly) return;
        const current = csepData.stepTwo.airMonitoringReadings || {};
        const contaminantState = current[contaminantId] || {
            check1: { value: '', time: '' }, check2: { value: '', time: '' }, check3: { value: '', time: '' }
        };
        const checkKey = `check${checkNum}`;
        scheduleSave({
            ...csepData,
            stepTwo: {
                ...csepData.stepTwo,
                airMonitoringReadings: {
                    ...current,
                    [contaminantId]: { ...contaminantState, [checkKey]: { ...contaminantState[checkKey], [field]: value } }
                }
            }
        });
    };

    const handleEnergizedSourcesChange = (_, newSources) => {
        if (isReadOnly) return;
        const currentSources = csepData.stepTwo.energizedSources || [];
        const sources = (newSources || []).map(src => {
            const srcId = src.id || src.ID;
            // Check if this source already exists (to preserve user's hazard selections)
            const existing = currentSources.find(s => (s.id || s.ID) === srcId);
            return {
                id: srcId,
                value: src.value || src.name,
                hazards: existing?.hazards || [] // Empty for new, preserve for existing
            };
        });
        updateStepTwo('energizedSources', sources);
    };

    const handleHazardsChange = (sourceId, newHazards) => {
        if (isReadOnly) return;
        const sources = (csepData.stepTwo.energizedSources || []).map(src =>
            (src.id || src.ID) === sourceId ? { ...src, hazards: newHazards } : src
        );
        updateStepTwo('energizedSources', sources);
    };

    const handleSignatureSave = (signatureData) => {
        if (!csepData || isReadOnly) return;
        scheduleSave({
            ...csepData,
            stepTwo: {
                ...csepData.stepTwo,
                entrySupervisorSignature: signatureData,
                entrySupervisorSignatureDate: dayjs().format('YYYY-MM-DD') // local day (not UTC)
            }
        }, { immediate: true });
        setSignatureModalOpen(false);
    };

    const handleSignatureClear = () => {
        if (!csepData || isReadOnly) return;
        scheduleSave({
            ...csepData,
            stepTwo: { ...csepData.stepTwo, entrySupervisorSignature: '', entrySupervisorSignatureDate: '' }
        });
    };

    const handleBack = async () => {
        pendingRecordRef.current = csepData;
        await flushSave();
        navigate('/csep/step-1', { state: { csep: csepData, isNew: false, viewMode } });
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            const now = new Date().toISOString();
            const updated = { ...csepData, status: 'submitted', meta: { ...csepData.meta, submittedAt: now } };

            // Send to API
            const response = await axiosInstance.post('/csep/submit', updated);

            if (response.data.success) {
                // Update local record with backend synced flag
                const synced = { ...updated, backendSynced: true, updatedAt: now };
                pendingRecordRef.current = synced;
                await flushSave();

                // If came from JHA, update JHA with linked CSEP and return
                const linkedJhaId = csepData.linkedJhaId || jhaId;
                if (fromJha && linkedJhaId) {
                    try {
                        const db = await initializeDB();
                        const jhaTx = db.transaction(JHA_STORE, 'readwrite');
                        const jhaStore = jhaTx.objectStore(JHA_STORE);
                        const jhaRecord = await jhaStore.get(linkedJhaId);

                        if (jhaRecord) {
                            // Find CSEP document and link it
                            const updatedDocsPermits = (jhaRecord.stepTwo?.docsPermits || []).map(doc => {
                                if (doc.value === 'Confined Space' || doc.specialType === 'csep') {
                                    return {
                                        ...doc,
                                        required: 'Yes Req',
                                        linkedCsepUuid: csepData.uuid,
                                        linkedCsepData: {
                                            uuid: csepData.uuid,
                                            location: csepData.stepOne?.location || csepData.stepTwo?.confinedSpaceLocation || '',
                                            jobCode: csepData.stepOne?.jobCode || '',
                                            supervisor: csepData.stepOne?.entrySupervisorName || '',
                                            submittedAt: now
                                        }
                                    };
                                }
                                return doc;
                            });

                            // Also transfer hazards and energized sources to JHA
                            const csepEnergySources = csepData.stepTwo?.energizedSources || [];
                            const existingEnergyRows = jhaRecord.stepThree?.energyActivityRows || [];
                            const csepWorkDescription = csepData.stepTwo?.workDescription || '';

                            // Add CSEP energy sources to JHA if not already there
                            const newEnergyRows = [...existingEnergyRows];
                            csepEnergySources.forEach(source => {
                                const exists = existingEnergyRows.some(row =>
                                    row.energySelections?.some(sel => sel.energyId === source.id)
                                );
                                if (!exists && source.hazards?.length > 0) {
                                    newEnergyRows.push({
                                        id: `csep-import-${Date.now()}-${source.id}`,
                                        workActivity: csepWorkDescription,
                                        actions: '',
                                        energySelections: [{
                                            id: `csep-sel-${Date.now()}-${source.id}`,
                                            energyKey: source.id,
                                            energyId: source.id,
                                            energyName: source.value || source.name || '',
                                            hazards: source.hazards || []
                                        }]
                                    });
                                }
                            });

                            const updatedJha = {
                                ...jhaRecord,
                                stepTwo: {
                                    ...jhaRecord.stepTwo,
                                    docsPermits: updatedDocsPermits
                                },
                                stepThree: {
                                    ...jhaRecord.stepThree,
                                    energyActivityRows: newEnergyRows,
                                    energyExposures: newEnergyRows
                                },
                                updatedAt: now
                            };

                            await jhaStore.put(updatedJha);
                            await jhaTx.done;

                            setSnackbar({ open: true, message: 'CSEP submitted and linked to JHA!', severity: 'success' });
                            // Navigate back to JHA Documents step (now step-3 after the step swap)
                            setTimeout(() => navigate('/jha/step-3', { state: { jha: updatedJha, isNew: false } }), 1500);
                            return;
                        }
                    } catch (jhaError) {
                        console.error('Error linking CSEP to JHA:', jhaError);
                        // Continue to normal navigation if JHA update fails
                    }
                }

                setSnackbar({ open: true, message: 'CSEP submitted successfully!', severity: 'success' });
                // Navigate after short delay to show message
                setTimeout(() => navigate('/csep'), 1500);
            } else {
                throw new Error(response.data.error || 'Failed to submit CSEP');
            }
        } catch (error) {
            console.error('CSEP submit error:', error);
            setSnackbar({ open: true, message: 'Failed to submit: ' + (error.response?.data?.error || error.message), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading || !csepData) {
        return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
    }

    // Wrap return in LocalizationProvider for DateTimePicker

    const stepTwo = csepData.stepTwo;
    const stepOne = csepData.stepOne;
    const hasSignature = Boolean(stepTwo.entrySupervisorSignature);

    const selectedEnergySources = (stepTwo.energizedSources || []).map(src => {
        const found = energyList.find(e => (e.id || e.ID) === (src.id || src.ID));
        return found || src;
    });

    const cellSx = { py: 0.25, px: 0.5, fontSize: '0.7rem' };
    const headerCellSx = { ...cellSx, fontWeight: 600, backgroundColor: '#f5f5f5' };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ height: 'calc(100vh - 64px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CsepStepHeader currentStep={2} csepData={csepData} />

            <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
                {/* Title */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', textAlign: 'center', mb: 0.5 }}>
                    Attachment 7B - CONFINED SPACE ENTRY PERMIT
                </Typography>

                {/* Description Section */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>DESCRIPTION</Typography>
                    <Stack spacing={0.75}>
                        {/* Row 1: Permit type + Project + Subcontractor (DHG) + Location */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <ToggleButtonGroup value={stepTwo.permitType} exclusive onChange={(_, val) => val && updateStepTwo('permitType', val)} size="small" disabled={isReadOnly} sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.7rem', height: 40 } }}>
                                {PERMIT_TYPES.map(pt => <ToggleButton key={pt.id} value={pt.id}>{pt.label}</ToggleButton>)}
                            </ToggleButtonGroup>
                            <TextField label="Project" value={stepTwo.project || stepOne.jobName || ''} onChange={(e) => updateStepTwo('project', e.target.value)} size="small" sx={{ flex: 1 }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} InputLabelProps={{ sx: { fontSize: '0.75rem' } }} />
                            <TextField label="Subcontractor" value={TENANT_COMPANY_CODE_SHORT} size="small" sx={{ flex: 0.7 }} InputProps={{ readOnly: true, sx: { fontSize: '0.75rem' } }} InputLabelProps={{ sx: { fontSize: '0.75rem' } }} />
                            <TextField label="Location" value={stepTwo.confinedSpaceLocation || stepOne.location || ''} onChange={(e) => updateStepTwo('confinedSpaceLocation', e.target.value)} size="small" sx={{ flex: 1 }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} InputLabelProps={{ sx: { fontSize: '0.75rem' } }} />
                        </Stack>
                        {/* Row 2: Entry DateTime + Space Type chips */}
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <DateTimePicker
                                label="Entry Date/Time"
                                value={stepTwo.entryDateTime ? dayjs(stepTwo.entryDateTime) : null}
                                onChange={(newValue) => updateStepTwo('entryDateTime', newValue ? newValue.toISOString() : '')}
                                disabled={isReadOnly}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { width: 210 },
                                        InputProps: { sx: { fontSize: '0.75rem' } },
                                        InputLabelProps: { sx: { fontSize: '0.75rem' } }
                                    }
                                }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, ml: 1 }}>Space Type:</Typography>
                            <Stack direction="row" spacing={0.25} flexWrap="wrap" sx={{ flex: 1 }}>
                                {CONFINED_SPACE_TYPES.filter(t => t.id !== 'other').map(type => (
                                    <Chip key={type.id} label={type.label} onClick={() => !isReadOnly && toggleConfinedSpaceType(type.id)}
                                        color={(stepTwo.confinedSpaceTypes || []).includes(type.id) ? 'primary' : 'default'}
                                        variant={(stepTwo.confinedSpaceTypes || []).includes(type.id) ? 'filled' : 'outlined'}
                                        disabled={isReadOnly} size="small" sx={{ fontSize: '0.65rem', height: 24 }} />
                                ))}
                                <Chip label="Other" onClick={() => !isReadOnly && toggleConfinedSpaceType('other')}
                                    color={(stepTwo.confinedSpaceTypes || []).includes('other') ? 'primary' : 'default'}
                                    variant={(stepTwo.confinedSpaceTypes || []).includes('other') ? 'filled' : 'outlined'}
                                    disabled={isReadOnly} size="small" sx={{ fontSize: '0.65rem', height: 24 }} />
                                {(stepTwo.confinedSpaceTypes || []).includes('other') && (
                                    <TextField value={stepTwo.confinedSpaceTypeOther || ''} onChange={(e) => updateStepTwo('confinedSpaceTypeOther', e.target.value)} size="small" placeholder="Specify..." disabled={isReadOnly} sx={{ width: 100, '& .MuiInputBase-root': { height: 24 } }} InputProps={{ sx: { fontSize: '0.7rem' } }} />
                                )}
                            </Stack>
                        </Stack>
                        {/* Row 3: Location of Confined Space + Work Description */}
                        <Stack direction="row" spacing={0.5}>
                            <TextField label="Location of Confined Space" value={stepTwo.locationOfConfinedSpace || ''} onChange={(e) => updateStepTwo('locationOfConfinedSpace', e.target.value)} size="small" sx={{ flex: 1 }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} InputLabelProps={{ sx: { fontSize: '0.75rem' } }} />
                            <TextField label="Work Description / Purpose" value={stepTwo.workDescription} onChange={(e) => updateStepTwo('workDescription', e.target.value)} size="small" sx={{ flex: 2 }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} InputLabelProps={{ sx: { fontSize: '0.75rem' } }} />
                        </Stack>
                    </Stack>
                </Paper>

                {/* Hazards & Energized Sources */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>HAZARDS & ENERGIZED SOURCES</Typography>
                    <Autocomplete multiple options={energyList} value={selectedEnergySources} onChange={handleEnergizedSourcesChange}
                        getOptionLabel={(option) => option.value || option.name || ''}
                        isOptionEqualToValue={(option, value) => (option.id || option.ID) === (value.id || value.ID)}
                        filterSelectedOptions disabled={isReadOnly} size="small"
                        renderInput={(params) => <TextField {...params} placeholder="Select energized sources..." size="small" InputProps={{ ...params.InputProps, sx: { fontSize: '0.75rem' } }} />}
                        sx={{ mb: 0.5 }} />
                    {(stepTwo.energizedSources || []).map((source, idx) => {
                        const energyOption = energyList.find(e => (e.id || e.ID) === (source.id || source.ID));
                        const hazardOptions = energyOption?.hazards || [];
                        return (
                            <Box key={source.id || idx} sx={{ pl: 1, borderLeft: '2px solid #1976d2', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500 }}>{source.value || source.name}</Typography>
                                {hazardOptions.length > 0 && (
                                    <Autocomplete multiple options={hazardOptions} value={source.hazards || []}
                                        onChange={(_, newVal) => handleHazardsChange(source.id || source.ID, newVal)}
                                        getOptionLabel={(opt) => { const t = opt.name || opt.value || ''; const i = t.indexOf(':'); return i > 0 ? t.substring(0, i).trim() : t; }}
                                        isOptionEqualToValue={(opt, val) => (opt.id || opt.name) === (val.id || val.name)}
                                        disableCloseOnSelect
                                        renderTags={(value, getTagProps) => value.map((option, index) => {
                                            const { key, ...chipProps } = getTagProps({ index });
                                            const t = option.name || option.value || ''; const i = t.indexOf(':');
                                            return <Chip key={key} label={i > 0 ? t.substring(0, i).trim() : t} size="small" {...chipProps} sx={{ fontSize: '0.65rem', height: 18 }} />;
                                        })}
                                        renderInput={(params) => <TextField {...params} size="small" placeholder="Hazards..." InputProps={{ ...params.InputProps, sx: { fontSize: '0.7rem' } }} />}
                                        disabled={isReadOnly} size="small" />
                                )}
                            </Box>
                        );
                    })}
                </Paper>

                {/* Verifications */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>VERIFICATIONS</Typography>
                    <Stack spacing={0.5}>
                        {VERIFICATION_ITEMS.map(item => {
                            const verif = stepTwo.verifications?.[item.id] || { date: '', signature: '', signedBy: '' };
                            const today = dayjs().format('YYYY-MM-DD'); // local day (not UTC)
                            const hasSig = Boolean(verif.signature);
                            return (
                                <Box key={item.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.75, backgroundColor: hasSig ? '#f9fdf9' : '#fff' }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {/* Item Label */}
                                        <Typography variant="caption" sx={{ fontWeight: 500, flex: 1, minWidth: 160, fontSize: '0.75rem' }}>
                                            {item.label}
                                        </Typography>
                                        {/* Date */}
                                        <TextField
                                            type="date"
                                            value={verif.date || today}
                                            onChange={(e) => updateVerification(item.id, 'date', e.target.value)}
                                            size="small"
                                            disabled={isReadOnly}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { fontSize: '0.7rem', cursor: 'pointer' }, onClick: (e) => e.target.showPicker?.() }}
                                            sx={{ width: 130, '& .MuiInputBase-root': { height: 32 } }}
                                        />
                                        {/* Signature Area */}
                                        <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {hasSig ? (
                                                <>
                                                    <Box
                                                        onClick={() => !isReadOnly && handleVerificationSign(item.id)}
                                                        sx={{
                                                            border: '1px solid #c5e1c6',
                                                            borderRadius: 1,
                                                            p: 0.25,
                                                            backgroundColor: '#fff',
                                                            cursor: isReadOnly ? 'default' : 'pointer',
                                                            '&:hover': !isReadOnly ? { borderColor: '#1976d2', backgroundColor: '#f5f9ff' } : {}
                                                        }}
                                                        title={isReadOnly ? '' : 'Click to edit'}
                                                    >
                                                        <img src={verif.signature} alt="Sig" style={{ maxWidth: 80, maxHeight: 28, display: 'block' }} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={verif.signedBy}>
                                                        {verif.signedBy}
                                                    </Typography>
                                                    {!isReadOnly && (
                                                        <Button variant="text" color="error" size="small" onClick={() => handleVerificationSignatureClear(item.id)} sx={{ fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0 }}>
                                                            ×
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleVerificationSign(item.id)}
                                                    disabled={isReadOnly}
                                                    sx={{ fontSize: '0.65rem', py: 0.25, px: 1, textTransform: 'none', borderStyle: 'dashed' }}
                                                >
                                                    Sign ({stepOne.entrySupervisorName || 'Supervisor'})
                                                </Button>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>

                {/* Special Requirements - 2 columns like paper form */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>SPECIAL REQUIREMENTS (Completed & Reviewed Prior to Entry)</Typography>
                    <Grid container spacing={0}>
                        {/* Left Column */}
                        <Grid item xs={6} sx={{ borderRight: '1px solid #e0e0e0' }}>
                            <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 0.5, border: '1px solid #e0e0e0', fontSize: '0.75rem' } }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}></TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 44, textAlign: 'center', p: 0.25 }}>Req</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 44, textAlign: 'center', p: 0.25 }}>Ver</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {SPECIAL_REQUIREMENTS.slice(0, 9).map(req => {
                                        const reqState = stepTwo.specialRequirements?.[req.id] || { required: false, verified: false };
                                        return (
                                            <TableRow key={req.id} sx={{ minHeight: 44 }}>
                                                <TableCell sx={{ py: 0.5 }}>{req.label}</TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => !isReadOnly && toggleSpecialRequirement(req.id, 'required')}>
                                                    <Checkbox checked={reqState.required} disabled={isReadOnly} sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => !isReadOnly && toggleSpecialRequirement(req.id, 'verified')}>
                                                    <Checkbox checked={reqState.verified} disabled={isReadOnly} color="success" sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* Other in left column */}
                                    {(stepTwo.otherRequirements || []).filter((_, i) => i % 2 === 0).map((req, idx) => {
                                        const realIdx = idx * 2;
                                        return (
                                            <TableRow key={`other-l-${realIdx}`} sx={{ backgroundColor: '#fffde7', minHeight: 44 }}>
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <TextField value={req.name || ''} onChange={(e) => { const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], name: e.target.value }; updateStepTwo('otherRequirements', updated); }} size="small" placeholder="Other..." disabled={isReadOnly} sx={{ flex: 1, '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } }} />
                                                        {!isReadOnly && <IconButton size="small" onClick={() => updateStepTwo('otherRequirements', (stepTwo.otherRequirements || []).filter((_, i) => i !== realIdx))}>×</IconButton>}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => { if (isReadOnly) return; const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], required: !req.required }; updateStepTwo('otherRequirements', updated); }}>
                                                    <Checkbox checked={req.required || false} disabled={isReadOnly} sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => { if (isReadOnly) return; const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], verified: !req.verified }; updateStepTwo('otherRequirements', updated); }}>
                                                    <Checkbox checked={req.verified || false} disabled={isReadOnly} color="success" sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Grid>
                        {/* Right Column */}
                        <Grid item xs={6}>
                            <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 0.5, border: '1px solid #e0e0e0', fontSize: '0.75rem' } }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}></TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 44, textAlign: 'center', p: 0.25 }}>Req</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 44, textAlign: 'center', p: 0.25 }}>Ver</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {SPECIAL_REQUIREMENTS.slice(9).map(req => {
                                        const reqState = stepTwo.specialRequirements?.[req.id] || { required: false, verified: false };
                                        const isRespirator = req.id === 'respiratorsRequired';
                                        return (
                                            <TableRow key={req.id} sx={{ minHeight: 44 }}>
                                                <TableCell sx={{ py: 0.5 }}>
                                                    {isRespirator ? (
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <span>Respirators Req'd</span>
                                                            <TextField value={stepTwo.respiratorType || ''} onChange={(e) => updateStepTwo('respiratorType', e.target.value)} size="small" placeholder="Type" disabled={isReadOnly} sx={{ width: 70, '& .MuiInputBase-root': { height: 28, fontSize: '0.7rem' } }} />
                                                        </Stack>
                                                    ) : req.label}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => !isReadOnly && toggleSpecialRequirement(req.id, 'required')}>
                                                    <Checkbox checked={reqState.required} disabled={isReadOnly} sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => !isReadOnly && toggleSpecialRequirement(req.id, 'verified')}>
                                                    <Checkbox checked={reqState.verified} disabled={isReadOnly} color="success" sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* Other in right column */}
                                    {(stepTwo.otherRequirements || []).filter((_, i) => i % 2 === 1).map((req, idx) => {
                                        const realIdx = idx * 2 + 1;
                                        return (
                                            <TableRow key={`other-r-${realIdx}`} sx={{ backgroundColor: '#fffde7', minHeight: 44 }}>
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <TextField value={req.name || ''} onChange={(e) => { const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], name: e.target.value }; updateStepTwo('otherRequirements', updated); }} size="small" placeholder="Other..." disabled={isReadOnly} sx={{ flex: 1, '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } }} />
                                                        {!isReadOnly && <IconButton size="small" onClick={() => updateStepTwo('otherRequirements', (stepTwo.otherRequirements || []).filter((_, i) => i !== realIdx))}>×</IconButton>}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => { if (isReadOnly) return; const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], required: !req.required }; updateStepTwo('otherRequirements', updated); }}>
                                                    <Checkbox checked={req.required || false} disabled={isReadOnly} sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', p: 0 }} onClick={() => { if (isReadOnly) return; const updated = [...(stepTwo.otherRequirements || [])]; updated[realIdx] = { ...updated[realIdx], verified: !req.verified }; updateStepTwo('otherRequirements', updated); }}>
                                                    <Checkbox checked={req.verified || false} disabled={isReadOnly} color="success" sx={{ p: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                    {/* Add Other button */}
                    {!isReadOnly && (
                        <Button size="small" variant="text" onClick={() => updateStepTwo('otherRequirements', [...(stepTwo.otherRequirements || []), { name: '', required: false, verified: false }])} sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                            + Add Other
                        </Button>
                    )}
                    {/* Attendants */}
                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #e0e0e0' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Attendant(s) Name(s):</Typography>
                            {!isReadOnly && (
                                <Button size="small" variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setAttendantsModalOpen(true)} sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75 }}>Select</Button>
                            )}
                            {(stepTwo.attendants || []).map(att => (
                                <Chip key={att.id} label={att.name} size="small" onDelete={!isReadOnly ? () => removeAttendant(att.id) : undefined} sx={{ fontSize: '0.7rem', height: 22 }} color={att.type === 'temp' ? 'warning' : 'default'} />
                            ))}
                            {(!stepTwo.attendants || stepTwo.attendants.length === 0) && <Typography variant="caption" color="text.secondary">None selected</Typography>}
                        </Stack>
                    </Box>
                </Paper>

                {/* Air Monitoring */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>AIR MONITORING</Typography>
                    <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
                        <Grid item xs={4} sm={2}><TextField label="Make" value={stepTwo.airMonitoringEquipment?.make || ''} onChange={(e) => updateNestedField('airMonitoringEquipment', 'make', e.target.value)} size="small" fullWidth disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.7rem' } }} InputLabelProps={{ sx: { fontSize: '0.7rem' } }} /></Grid>
                        <Grid item xs={4} sm={2}><TextField label="Model" value={stepTwo.airMonitoringEquipment?.model || ''} onChange={(e) => updateNestedField('airMonitoringEquipment', 'model', e.target.value)} size="small" fullWidth disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.7rem' } }} InputLabelProps={{ sx: { fontSize: '0.7rem' } }} /></Grid>
                        <Grid item xs={4} sm={2}><TextField label="ID#" value={stepTwo.airMonitoringEquipment?.equipmentId || ''} onChange={(e) => updateNestedField('airMonitoringEquipment', 'equipmentId', e.target.value)} size="small" fullWidth disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.7rem' } }} InputLabelProps={{ sx: { fontSize: '0.7rem' } }} /></Grid>
                        <Grid item xs={4} sm={2}>
                            <TextField
                                label="Calibration Date"
                                type="date"
                                value={stepTwo.airMonitoringEquipment?.calibrationDate || ''}
                                onChange={(e) => updateNestedField('airMonitoringEquipment', 'calibrationDate', e.target.value)}
                                size="small"
                                fullWidth
                                disabled={isReadOnly}
                                InputLabelProps={{ shrink: true, sx: { fontSize: '0.7rem' } }}
                                InputProps={{ sx: { fontSize: '0.7rem' } }}
                                inputProps={{ onClick: (e) => !isReadOnly && e.target.showPicker?.() }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={2}><TextField label="Calibrated By" value={stepTwo.airMonitoringEquipment?.calibratedBy || ''} onChange={(e) => updateNestedField('airMonitoringEquipment', 'calibratedBy', e.target.value)} size="small" fullWidth disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.7rem' } }} InputLabelProps={{ sx: { fontSize: '0.7rem' } }} /></Grid>
                        <Grid item xs={4} sm={2}><TextField label="Checked By" value={stepTwo.atmosphereCheckedBy || stepOne.entrySupervisorName || ''} size="small" fullWidth InputProps={{ readOnly: true, sx: { fontSize: '0.7rem', cursor: isReadOnly ? 'default' : 'pointer' } }} InputLabelProps={{ sx: { fontSize: '0.7rem' } }} onClick={() => !isReadOnly && setCheckedByModalOpen(true)} /></Grid>
                    </Grid>

                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.25, fontSize: '0.6rem' }}>* 1st CHECK PRIOR TO ENTRY</Typography>
                    <TableContainer>
                        <Table size="small" sx={{ '& td, & th': { border: '1px solid #e0e0e0' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellSx}>Contaminant</TableCell>
                                    <TableCell sx={{ ...headerCellSx, width: 100 }}>PELs</TableCell>
                                    <TableCell sx={{ ...headerCellSx, textAlign: 'center', minWidth: 160 }}>1st Check</TableCell>
                                    <TableCell sx={{ ...headerCellSx, textAlign: 'center', minWidth: 160 }}>2nd Check</TableCell>
                                    <TableCell sx={{ ...headerCellSx, textAlign: 'center', minWidth: 160 }}>3rd Check</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {AIR_CONTAMINANTS.filter(c => c.id !== 'other').map(contaminant => {
                                    const readings = stepTwo.airMonitoringReadings?.[contaminant.id] || {
                                        check1: { value: '', time: '' }, check2: { value: '', time: '' }, check3: { value: '', time: '' }
                                    };
                                    return (
                                        <TableRow key={contaminant.id}>
                                            <TableCell sx={cellSx}>{contaminant.label}</TableCell>
                                            <TableCell sx={cellSx}><Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{contaminant.pel}</Typography></TableCell>
                                            {[1, 2, 3].map(checkNum => {
                                                const check = readings[`check${checkNum}`] || { value: '', time: '' };
                                                return (
                                                    <TableCell key={checkNum} sx={{ ...cellSx, minWidth: 160 }}>
                                                        <Stack spacing={0.5}>
                                                            <TextField value={check.value} onChange={(e) => updateAirReading(contaminant.id, checkNum, 'value', e.target.value)} size="small" placeholder="Value" disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} sx={{ '& .MuiInputBase-root': { height: 32 } }} />
                                                            <DateTimePicker
                                                                value={check.time ? dayjs(check.time) : null}
                                                                onChange={(newValue) => updateAirReading(contaminant.id, checkNum, 'time', newValue ? newValue.toISOString() : '')}
                                                                disabled={isReadOnly}
                                                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } } } }}
                                                            />
                                                        </Stack>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                                {/* Other contaminants - dynamic rows */}
                                {(stepTwo.otherContaminants || []).map((other, idx) => (
                                    <TableRow key={`other-${idx}`} sx={{ backgroundColor: '#fffde7' }}>
                                        <TableCell sx={cellSx}>
                                            <Stack direction="row" spacing={0.25} alignItems="center">
                                                <TextField value={other.name || ''} onChange={(e) => { const updated = [...(stepTwo.otherContaminants || [])]; updated[idx] = { ...updated[idx], name: e.target.value }; updateStepTwo('otherContaminants', updated); }} size="small" placeholder="Other..." disabled={isReadOnly} sx={{ flex: 1, '& .MuiInputBase-root': { height: 22, fontSize: '0.65rem' } }} />
                                                {!isReadOnly && <IconButton size="small" sx={{ p: 0 }} onClick={() => updateStepTwo('otherContaminants', (stepTwo.otherContaminants || []).filter((_, i) => i !== idx))}>×</IconButton>}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            <TextField value={other.pel || ''} onChange={(e) => { const updated = [...(stepTwo.otherContaminants || [])]; updated[idx] = { ...updated[idx], pel: e.target.value }; updateStepTwo('otherContaminants', updated); }} size="small" placeholder="PEL" disabled={isReadOnly} sx={{ width: '100%', '& .MuiInputBase-root': { height: 22, fontSize: '0.65rem' } }} />
                                        </TableCell>
                                        {[1, 2, 3].map(checkNum => {
                                            const checkKey = `check${checkNum}`;
                                            const check = other[checkKey] || { value: '', time: '' };
                                            return (
                                                <TableCell key={checkNum} sx={{ ...cellSx, minWidth: 160 }}>
                                                    <Stack spacing={0.5}>
                                                        <TextField value={check.value || ''} onChange={(e) => { const updated = [...(stepTwo.otherContaminants || [])]; updated[idx] = { ...updated[idx], [checkKey]: { ...check, value: e.target.value } }; updateStepTwo('otherContaminants', updated); }} size="small" placeholder="Value" disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} sx={{ '& .MuiInputBase-root': { height: 32 } }} />
                                                        <DateTimePicker
                                                            value={check.time ? dayjs(check.time) : null}
                                                            onChange={(newValue) => { const updated = [...(stepTwo.otherContaminants || [])]; updated[idx] = { ...updated[idx], [checkKey]: { ...check, time: newValue ? newValue.toISOString() : '' } }; updateStepTwo('otherContaminants', updated); }}
                                                            disabled={isReadOnly}
                                                            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } } } }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Add Other button */}
                    {!isReadOnly && (
                        <Button size="small" variant="text" onClick={() => updateStepTwo('otherContaminants', [...(stepTwo.otherContaminants || []), { name: '', pel: '', check1: { value: '', time: '' }, check2: { value: '', time: '' }, check3: { value: '', time: '' } }])} sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                            + Add Other
                        </Button>
                    )}

                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>In Case of Emergency Call:</Typography>
                        <TextField value={stepTwo.emergencyContact || '911'} onChange={(e) => updateStepTwo('emergencyContact', e.target.value)} size="small" sx={{ width: 60, '& .MuiInputBase-root': { height: 26 } }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem', fontWeight: 600 } }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>or</Typography>
                        <TextField value={stepTwo.emergencyContactOther || ''} onChange={(e) => updateStepTwo('emergencyContactOther', e.target.value)} size="small" placeholder="Other number" sx={{ flex: 1, '& .MuiInputBase-root': { height: 26 } }} disabled={isReadOnly} InputProps={{ sx: { fontSize: '0.75rem' } }} />
                    </Stack>
                </Paper>

                {/* Authorization */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                        <DrawIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        AUTHORIZATION - Entry Supervisor Signature
                    </Typography>
                    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', border: hasSignature ? '1px solid #4caf50' : '1px solid #e0e0e0' }}>
                        {/* Header */}
                        <Box sx={{ backgroundColor: hasSignature ? '#e8f5e9' : '#f5f5f5', borderBottom: '1px solid #e0e0e0', p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ backgroundColor: hasSignature ? '#4caf50' : '#9e9e9e', color: 'white', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {hasSignature ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <RadioButtonUnchecked sx={{ fontSize: 18 }} />}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                    {stepOne.entrySupervisorName || 'Entry Supervisor'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    Entry Supervisor
                                </Typography>
                            </Box>
                            <Chip
                                icon={hasSignature ? <CheckCircleIcon /> : <RadioButtonUnchecked />}
                                label={hasSignature ? 'Signed' : 'Pending'}
                                color={hasSignature ? 'success' : 'default'}
                                size="small"
                                variant={hasSignature ? 'filled' : 'outlined'}
                            />
                        </Box>
                        {/* Signature Content */}
                        <Box sx={{ p: 1.5 }}>
                            {hasSignature ? (
                                <Box>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Digital Signature:</Typography>
                                            <Paper sx={{ border: '1px solid #4caf50', borderRadius: 1, p: 0.75, backgroundColor: '#f1f8e9', display: 'inline-block' }}>
                                                <img src={stepTwo.entrySupervisorSignature} alt="Signature" style={{ maxWidth: 200, maxHeight: 60, display: 'block' }} />
                                            </Paper>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Date:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{stepTwo.entrySupervisorSignatureDate || '-'}</Typography>
                                        </Box>
                                        {!isReadOnly && (
                                            <Stack direction="row" spacing={0.5}>
                                                <Button variant="outlined" size="small" onClick={() => setSignatureModalOpen(true)} sx={{ fontSize: '0.75rem' }}>Update</Button>
                                                <Button variant="outlined" color="error" size="small" onClick={handleSignatureClear} sx={{ fontSize: '0.75rem' }}>Remove</Button>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Signature required to authorize entry
                                    </Typography>
                                    {!isReadOnly && (
                                        <Button variant="contained" color="primary" startIcon={<DrawIcon />} onClick={() => setSignatureModalOpen(true)} sx={{ px: 3 }}>
                                            Sign Now
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Paper>
            </Box>

            {/* Bottom Navigation */}
            <Box sx={{ backgroundColor: '#fff', borderTop: '1px solid #e0e0e0', px: 1, py: 0.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={handleBack}>Back</Button>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">{saving ? 'Saving...' : ''}</Typography>
                        {!isReadOnly && (
                            <Button variant="contained" color="success" size="small" endIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />} onClick={handleSubmit} disabled={saving || !hasSignature}>{saving ? 'Submitting...' : 'Submit'}</Button>
                        )}
                    </Stack>
                </Stack>
            </Box>

            <SignatureModal allowAuto open={signatureModalOpen} onClose={() => setSignatureModalOpen(false)} onSave={handleSignatureSave} existingSignature={stepTwo.entrySupervisorSignature || ''} employeeName={stepOne.entrySupervisorName || ''} />
            <SignatureModal allowAuto open={verificationSignModalOpen} onClose={() => { setVerificationSignModalOpen(false); setActiveVerificationId(null); }} onSave={handleVerificationSignatureSave} existingSignature={activeVerificationId ? (stepTwo.verifications?.[activeVerificationId]?.signature || '') : ''} employeeName={stepOne.entrySupervisorName || ''} />
            <SelectEmployeesAndTempsModal open={attendantsModalOpen} onClose={() => setAttendantsModalOpen(false)} onSelect={handleAttendantsSelect} alreadySelected={(stepTwo.attendants || []).map(a => ({ ...a, FullName: a.name, _type: a.type || 'employee' }))} />
            <SelectEmployeesAndTempsModal open={checkedByModalOpen} onClose={() => setCheckedByModalOpen(false)} onSelect={handleCheckedBySelect} singleSelect alreadySelected={[]} />

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </LocalizationProvider>
    );
}
